import { current } from "@reduxjs/toolkit";
import {
  setChartsAccountLayoutAllViews,
  setJournalLineLayoutAllViews,
  setPaymentTermsLayoutAllViews,
} from "../slice/accountSlice";
import {
  setCustomerLayoutAllViews,
  setCustomerSegmentLayoutAllViews,
  setCustomerTagLayoutAllViews,
} from "../slice/customerSlice";
import {
  setShopifyCatalogueLayoutAllViews,
  setShopifyLinkedProductsLayoutAllViews,
  setShopifyStoresLayoutAllViews,
  setSyncHistoryLayoutAllViews,
} from "../slice/integrationSlice";
import {
  setBillChargeTypeLayoutAllViews,
  setBillLayoutAllViews,
  setPrePaymentLayoutAllViews,
  setSaleInvoiceLayoutAllViews,
  setCustomerCreditNoteLayoutAllViews,
  setSupplierCreditNoteLayoutAllViews,
} from "../slice/invoiceSlice";
import { setCoManufacturingOrderLayoutAllViews } from "../slice/manufacturingOrderSlice";
import { setNotification } from "../slice/miscSlice";
import {
  setAllFilterCondition,
  setAllFilterField,
  setAllViews,
  setConsignmentsForRule,
  setMilestoneOrders,
  setSingleView,
  setStatusForRule,
  setViewAndDetailsColumns,
} from "../slice/newInterfaceSlice";
import { setPlannedOrdersLayoutAllViews } from "../slice/plannerSlice";
import { setPricelistLayoutAllViews } from "../slice/pricingSlice";
import {
  setBomsLayoutAllViews,
  setManufacturingOrderLayoutAllViews,
  setPeopleLayoutAllViews,
} from "../slice/productionSlice";
import {
  setPackLayoutAllViews,
  setProductAliasLayoutAllViews,
  setProductCategoryLayoutAllViews,
  setProductPlanLayoutAllViews,
  setProductsLayoutAllViews,
  setProductTagLayoutAllViews,
} from "../slice/productSlice";
import { setPromoLayoutAllViews } from "../slice/promoSlice";
import { patchPurchaseOrderInList } from "../slice/purchaseOrderSlice";
import {
  setAllViewsForPurchase,
  setMilestoneOrdersForPurchase,
} from "../slice/purchaseOrderSliceV2";
import {
  setReceiptAdjustmentLayoutAllViews,
  setReceiptsLayoutAllViews,
} from "../slice/receiptSlice";
import { setTransactionReasonLayoutAllViews } from "../slice/reportSlice";
import {
  setCustomerReturnLayoutAllViews,
  setQuoteLayoutAllViews,
  setSalesOrderLayoutAllViews,
  setSalesRepLayoutAllViews,
} from "../slice/salesSlice";
import {
  setAsnLayoutAllViews,
  setDeliveryLayoutAllViews,
  setEquipmentLayoutAllViews,
  setEquipmentTypeLayoutAllViews,
  setShipmentLegLayoutAllViews,
  setShipmentV1LayoutAllViews,
} from "../slice/shipmentSlice";
import { setSupplierLayoutAllViews, setWarehouseLayoutAllViews } from "../slice/supplierSlice";
import {
  setProductTaxClassificationLayoutAllViews,
  setTaxCategoryLayoutAllViews,
  setTaxLayoutAllViews,
} from "../slice/taxSlice";
import {
  setAllViewsForTransfer,
  setMilestoneOrdersForTransfer,
  setViewsForManufacturingOrderAllocation,
  setViewsForPurchaseAllocation,
  setViewsForSalesOrderAllocation,
  setViewsForTransferAllocation,
  updateFilterTransferByMilestone,
} from "../slice/TransferOrderSliceV2";
import { setUnitOfMeasureLayoutAllViews } from "../slice/unitOfMeasureSlice";
import {
  setAllViewsForWarehouse,
  setForecastLayoutAllViews,
  setPicklistLayoutAllViews,
  setSiteLayoutAllViews,
  setStandardCostLayoutAllViews,
  setStockLocationLayoutAllViews,
  setStockPackLayoutAllViews,
  setStockZoneLayoutAllViews,
  setWarehouseCostingLayoutAllViews,
  setWarehouseCostingRangeLayoutAllViews,
  setWorkCenterLayoutAllViews,
  stockReplenishmentLayoutAllViews,
} from "../slice/warehouseSliceV2";
import { getNameForViewRule } from "../utils";
import { apiSlice } from "./apiSlice";
import {
  dispatchRemoveNote,
  dispatchSetNotes,
  dispatchUpdateNote,
  getNotesFromState,
  patchRecordAfterNoteDelete,
  resolveNoteOrderType,
} from "./noteCache";
import { operationsApiSlice } from "./operationsApiSlice";
import { fileResponseHandler } from "./utils";

export const newInterfaceApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    addMilestone: builder.mutation({
      query: data => ({
        url: `/api_interface/milestone/`,
        method: "POST",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(res => dispatch(newInterfaceApiSlice.util.invalidateTags(["milestonesUpdateList"])))
          .catch(err => {});
      },
    }),
    getMilestones: builder.query({
      query: params => (params ? `api_interface/milestone/?${params}` : "api_interface/milestone/"),
      onQueryStarted: (params, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            if (params === "module=purchase_order") {
              dispatch(setMilestoneOrdersForPurchase(result.data));
            } else if (params === "module=transfer_order") {
              dispatch(setMilestoneOrdersForTransfer(result.data));
            } else if (params === "module=consignment") {
              dispatch(setMilestoneOrders(result.data));
            }
          })
          .catch(error => {});
      },
      providesTags: _ => ["milestonesUpdateList"],
    }),
    updateMilestoneDetails: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api_interface/milestone/${id}/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(
            newInterfaceApiSlice.util.invalidateTags([
              "milestonesUpdateList",
              "consignmentList",
              "filteredConsignmentList",
            ]),
          );
        } catch (_) {
          // The edit form retains the input and displays the server error.
        }
      },
    }),
    deleteMilestone: builder.mutation({
      query: id => ({
        url: `/api_interface/milestone/${id}/`,
        method: "DELETE",
        body: {},
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        let type = "success";
        let message = "Done! The milestone has been removed successfully.";
        await queryFulfilled
          .then(() => {
            dispatch(
              newInterfaceApiSlice.util.invalidateTags([
                "milestonesUpdateList",
                "consignmentList",
                "filteredConsignmentList",
              ]),
            );
          })
          .catch(error => {
            type = "error";
            message = error.error?.data?.detail || "Deletion unsuccessful. Please try again.";
          });
        dispatch(setNotification({ type, message }));
      },
    }),

    /**
     * Deletes a note or task, optimistically patches local notes and list icons,
     * and invalidates relevant tags on success.
     *
     * @type {Object}
     */
    deleteNote: builder.mutation({
      query: ({ id }) => ({
        url: `/api_chat/note/${id}/`,
        method: "DELETE",
        body: {},
      }),
      onQueryStarted: async (
        { id, orderId, orderType, remainingCount },
        { dispatch, queryFulfilled, getState },
      ) => {
        const normalizedOrderType = resolveNoteOrderType(orderType);
        const previousNotes = getNotesFromState(getState(), normalizedOrderType);
        const deleted = previousNotes.find(note => note.id === id);
        const wasOpenTask = Boolean(deleted?.is_task && !deleted?.is_done);
        dispatchRemoveNote(dispatch, normalizedOrderType, id);
        const restoreListPatch = patchRecordAfterNoteDelete(
          dispatch,
          getState,
          orderId,
          normalizedOrderType,
          { remainingCount, wasOpenTask },
        );
        try {
          await queryFulfilled;
          if (normalizedOrderType === "shipment") {
            dispatch(apiSlice.util.invalidateTags(["consignmentList", "filteredConsignmentList"]));
            if (orderId) {
              dispatch(
                apiSlice.util.invalidateTags([{ type: "consignmentDetail", id: Number(orderId) }]),
              );
            }
          } else if (normalizedOrderType === "purchase") {
            dispatch(apiSlice.util.invalidateTags(["purchaseOrderList"]));
            if (orderId) {
              dispatch(
                apiSlice.util.invalidateTags([
                  { type: "purchaseOrderDetail", id: String(orderId) },
                ]),
              );
            }
          } else if (normalizedOrderType === "transfer") {
            dispatch(apiSlice.util.invalidateTags(["transferOrderList"]));
            if (orderId) {
              dispatch(
                apiSlice.util.invalidateTags([
                  { type: "transferOrderDetail", id: Number(orderId) },
                ]),
              );
            }
          }
          dispatch(operationsApiSlice.util.invalidateTags(["OperationsOverview"]));
        } catch {
          dispatchSetNotes(dispatch, normalizedOrderType, previousNotes);
          restoreListPatch();
        }
      },
    }),
    /**
     * Updates a note or task (e.g. text or completion state), updates optimistic state,
     * and invalidates relevant detail/list tags.
     *
     * @type {Object}
     */
    updateNote: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api_chat/note/${id}/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: async (
        { id, data, orderId, orderType },
        { dispatch, queryFulfilled, getState },
      ) => {
        const normalizedOrderType = resolveNoteOrderType(orderType);
        const previousNotes = getNotesFromState(getState(), normalizedOrderType);
        dispatchUpdateNote(dispatch, normalizedOrderType, { id, ...data });
        try {
          const { data: result } = await queryFulfilled;
          if (result) {
            dispatchUpdateNote(dispatch, normalizedOrderType, result);
          }
          if (normalizedOrderType === "shipment") {
            dispatch(apiSlice.util.invalidateTags(["consignmentList", "filteredConsignmentList"]));
            if (orderId) {
              dispatch(
                apiSlice.util.invalidateTags([{ type: "consignmentDetail", id: Number(orderId) }]),
              );
            }
          } else if (normalizedOrderType === "purchase") {
            dispatch(apiSlice.util.invalidateTags(["purchaseOrderList"]));
            if (orderId) {
              dispatch(
                apiSlice.util.invalidateTags([
                  { type: "purchaseOrderDetail", id: String(orderId) },
                ]),
              );
            }
          } else if (normalizedOrderType === "transfer") {
            dispatch(apiSlice.util.invalidateTags(["transferOrderList"]));
            if (orderId) {
              dispatch(
                apiSlice.util.invalidateTags([
                  { type: "transferOrderDetail", id: Number(orderId) },
                ]),
              );
            }
          }
          dispatch(operationsApiSlice.util.invalidateTags(["OperationsOverview"]));
        } catch {
          dispatchSetNotes(dispatch, normalizedOrderType, previousNotes);
        }
      },
    }),
    createView: builder.mutation({
      query: data => ({
        url: `/api_interface/view/`,
        method: "POST",
        body: data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(newInterfaceApiSlice.util.invalidateTags(["viewUpdate"]));
        } catch (_) {}
      },
    }),
    getView: builder.query({
      query: params => (params ? `/api_interface/view/?${params}` : "api_interface/view/"),
      onQueryStarted: (params, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            if (params === "module=purchase_order") {
              dispatch(setAllViewsForPurchase(result.data));
            } else if (params === "module=transfer_order") {
              dispatch(setAllViewsForTransfer(result.data));
            } else if (params === "module=consignment") {
              dispatch(setAllViews(result.data));
            } else if (params === "module=shipment" || params === "module=shipmentV1") {
              dispatch(setShipmentV1LayoutAllViews(result.data));
            } else if (params === "module=asn") {
              dispatch(setAsnLayoutAllViews(result.data));
            } else if (params === "module=shipment_leg") {
              dispatch(setShipmentLegLayoutAllViews(result.data));
            } else if (params === "module=po_allocation") {
              dispatch(setViewsForPurchaseAllocation(result.data));
            } else if (params === "module=tf_ord_allocation") {
              dispatch(setViewsForTransferAllocation(result.data));
            } else if (params === "module=so_allocation") {
              dispatch(setViewsForSalesOrderAllocation(result.data));
            } else if (params === "module=mo_allocation") {
              dispatch(setViewsForManufacturingOrderAllocation(result.data));
            } else if (
              params === "module=stock_summary" ||
              params === "module=stock_line" ||
              params === "module=stock_transaction"
            ) {
              dispatch(
                setAllViewsForWarehouse({ data: result.data, moduleType: params.split("=").pop() }),
              );
            } else if (params === "module=receipt") {
              dispatch(setReceiptsLayoutAllViews(result.data));
            } else if (params === "module=product") {
              dispatch(setProductsLayoutAllViews(result.data));
            } else if (params === "module=product_category") {
              dispatch(setProductCategoryLayoutAllViews(result.data));
            } else if (params === "module=product_tag") {
              dispatch(setProductTagLayoutAllViews(result.data));
            } else if (params === "module=product_alias") {
              dispatch(setProductAliasLayoutAllViews(result.data));
            } else if (params === "module=pack") {
              dispatch(setPackLayoutAllViews(result.data));
            } else if (params === "module=stock_pack") {
              dispatch(setStockPackLayoutAllViews(result.data));
            } else if (params === "module=warehouse") {
              dispatch(setWarehouseLayoutAllViews(result.data));
            } else if (params === "module=supplier") {
              dispatch(setSupplierLayoutAllViews(result.data));
            } else if (params === "module=whs_cost_range") {
              dispatch(setWarehouseCostingRangeLayoutAllViews(result.data));
            } else if (params === "module=forecast") {
              dispatch(setForecastLayoutAllViews(result.data));
            } else if (params === "module=whs_cost") {
              dispatch(setWarehouseCostingLayoutAllViews(result.data));
            } else if (params === "module=stock_zone") {
              dispatch(setStockZoneLayoutAllViews(result.data));
            } else if (params === "module=stock_location") {
              dispatch(setStockLocationLayoutAllViews(result.data));
            } else if (params === "module=product_plan") {
              dispatch(setProductPlanLayoutAllViews(result.data));
            } else if (params === "module=transaction_reason") {
              dispatch(setTransactionReasonLayoutAllViews(result.data));
            } else if (params === "module=standard_costing") {
              dispatch(setStandardCostLayoutAllViews(result.data));
            } else if (params === "module=tax_rate") {
              dispatch(setTaxLayoutAllViews(result.data));
            } else if (params === "module=tax_category") {
              dispatch(setTaxCategoryLayoutAllViews(result.data));
            } else if (params === "module=product_tax_classification") {
              dispatch(setProductTaxClassificationLayoutAllViews(result.data));
            } else if (params === "module=charts_account") {
              dispatch(setChartsAccountLayoutAllViews(result.data));
            } else if (params === "module=payment_term") {
              dispatch(setPaymentTermsLayoutAllViews(result.data));
            } else if (params === "module=journal_line") {
              dispatch(setJournalLineLayoutAllViews(result.data));
            } else if (params === "module=receipt_adjustment") {
              dispatch(setReceiptAdjustmentLayoutAllViews(result.data));
            } else if (params === "module=bill") {
              dispatch(setBillLayoutAllViews(result.data));
            } else if (params === "module=bill_charge_type") {
              dispatch(setBillChargeTypeLayoutAllViews(result.data));
            } else if (params === "module=sale_invoice") {
              dispatch(setSaleInvoiceLayoutAllViews(result.data));
            } else if (params === "module=integ_audit_log") {
              dispatch(setSyncHistoryLayoutAllViews(result.data));
            } else if (params === "module=shopify_store") {
              dispatch(setShopifyStoresLayoutAllViews(result.data));
            } else if (params === "module=shopify_catalogue") {
              dispatch(setShopifyCatalogueLayoutAllViews(result.data));
            } else if (params === "module=shopify_linked_product") {
              dispatch(setShopifyLinkedProductsLayoutAllViews(result.data));
            } else if (params === "module=supplier_credit_note") {
              dispatch(setSupplierCreditNoteLayoutAllViews(result.data));
            } else if (params === "module=customer_credit_note") {
              dispatch(setCustomerCreditNoteLayoutAllViews(result.data));
            } else if (params === "module=prepayment") {
              dispatch(setPrePaymentLayoutAllViews(result.data));
            } else if (params === "module=stock_replenishment") {
              dispatch(stockReplenishmentLayoutAllViews(result.data));
            } else if (params === "module=planned_order") {
              dispatch(setPlannedOrdersLayoutAllViews(result.data));
            } else if (params === "module=picklist") {
              dispatch(setPicklistLayoutAllViews(result.data));
            } else if (params === "module=customer") {
              dispatch(setCustomerLayoutAllViews(result.data));
            } else if (params === "module=customer_tag") {
              dispatch(setCustomerTagLayoutAllViews(result.data));
            } else if (params === "module=customer_segment") {
              dispatch(setCustomerSegmentLayoutAllViews(result.data));
            } else if (params === "module=unit_of_measure") {
              dispatch(setUnitOfMeasureLayoutAllViews(result.data));
            } else if (params === "module=equipment_type") {
              dispatch(setEquipmentTypeLayoutAllViews(result.data));
            } else if (params === "module=equipment") {
              dispatch(setEquipmentLayoutAllViews(result.data));
            } else if (params === "module=sales_rep") {
              dispatch(setSalesRepLayoutAllViews(result.data));
            } else if (params === "module=pricelist") {
              dispatch(setPricelistLayoutAllViews(result.data));
            } else if (params === "module=promo") {
              dispatch(setPromoLayoutAllViews(result.data));
            } else if (params === "module=sale_quot") {
              dispatch(setQuoteLayoutAllViews(result.data));
            } else if (params === "module=sale_order") {
              dispatch(setSalesOrderLayoutAllViews(result.data));
            } else if (params === "module=delivery") {
              dispatch(setDeliveryLayoutAllViews(result.data));
            } else if (params === "module=return") {
              dispatch(setCustomerReturnLayoutAllViews(result.data));
            } else if (params === "module=work_center") {
              dispatch(setWorkCenterLayoutAllViews(result.data));
            } else if (params === "module=site") {
              dispatch(setSiteLayoutAllViews(result.data));
            } else if (params === "module=people") {
              dispatch(setPeopleLayoutAllViews(result.data));
            } else if (params === "module=bom") {
              dispatch(setBomsLayoutAllViews(result.data));
            } else if (params === "module=manufacturing_order") {
              dispatch(setManufacturingOrderLayoutAllViews(result.data));
            } else if (params === "module=co_manufacturing_order") {
              dispatch(setCoManufacturingOrderLayoutAllViews(result.data));
            }
          })
          .catch(error => {});
      },
      providesTags: _ => ["viewUpdate"],
    }),
    getSingleView: builder.query({
      query: id => `api_interface/view/${id}`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setSingleView(result.data))).catch(error => {});
      },
    }),
    updateView: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api_interface/view/${id}/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(newInterfaceApiSlice.util.invalidateTags(["viewUpdate"]));
        } catch (_) {}
      },
    }),
    deleteView: builder.mutation({
      query: id => ({
        url: `/api_interface/view/${id}`,
        method: "DELETE",
        body: {},
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(_ => {
            dispatch(newInterfaceApiSlice.util.invalidateTags(["viewUpdate"]));
          })
          .catch(() => {});
      },
    }),
    handleMilestoneView: builder.mutation({
      query: ({ id, data }) => ({
        url:
          id === "Uncategorized"
            ? `/api_interface/milestone/bulk_remove_relation/`
            : `/api_interface/milestone/${Number(id)}/bulk_update_relation/`,
        method: "PUT",
        body: data,
      }),
      onQueryStarted: async (dataFromQuery, { dispatch, getState, queryFulfilled }) => {
        const {
          id: destinationId,
          data: { ids, module },
        } = dataFromQuery;

        /**
         * Writes the destination milestone onto loaded consignment list pages.
         * @param {?Object} milestone The destination milestone, or null for Uncategorized.
         * @return {function(): void} Restores prior milestone values for the moved ids.
         */
        const applyConsignmentMilestone = milestone => {
          const selected = new Set(ids);
          const listEndpoints = ["getConsignments", "getFilterConsignmentsByRule"];
          const restores = [];
          newInterfaceApiSlice.util
            .selectInvalidatedBy(getState(), ["consignmentList", "filteredConsignmentList"])
            .forEach(({ endpointName, originalArgs }) => {
              if (!listEndpoints.includes(endpointName)) {
                return;
              }
              const originals = new Map();
              dispatch(
                newInterfaceApiSlice.util.updateQueryData(endpointName, originalArgs, draft => {
                  draft.results?.forEach(record => {
                    if (!selected.has(record.id)) {
                      return;
                    }
                    originals.set(record.id, record.milestone ? current(record.milestone) : null);
                    record.milestone = milestone;
                  });
                }),
              );
              restores.push(() =>
                dispatch(
                  newInterfaceApiSlice.util.updateQueryData(endpointName, originalArgs, draft => {
                    draft.results?.forEach(record => {
                      if (!originals.has(record.id)) {
                        return;
                      }
                      record.milestone = originals.get(record.id);
                    });
                  }),
                ),
              );
            });
          return () => restores.forEach(restore => restore());
        };

        let restore = null;
        if (module === "consignment") {
          const destination =
            destinationId === "Uncategorized"
              ? null
              : (getState().newInterface?.milestoneList || []).find(
                  item => Number(item.id) === Number(destinationId),
                );
          if (destination !== undefined) {
            restore = applyConsignmentMilestone(destination);
          }
        }

        /**
         * The purchase board reads each order's milestone from the list.
         * Move those records immediately, and put them back if the save fails.
         * @return {function(): void} Restores the previous milestone on each moved order.
         */
        const applyPurchaseOrderMilestone = () => {
          const poMilestoneList = getState().newPurchaseOrder?.milestoneList || [];
          const destinationMilestone =
            destinationId === "Uncategorized"
              ? null
              : poMilestoneList.find(item => Number(item.id) === Number(destinationId)) || {
                  id: Number(destinationId),
                };
          const results = getState().purchaseOrder?.purchaseOrders?.results || [];
          const originals = ids
            .map(orderId => results.find(order => Number(order.id) === Number(orderId)))
            .filter(Boolean)
            .map(order => ({
              id: order.id,
              milestone: order.milestone ?? null,
            }));
          originals.forEach(order => {
            dispatch(
              patchPurchaseOrderInList({
                id: order.id,
                milestone: destinationMilestone,
              }),
            );
          });
          const selected = new Set(ids.map(Number));
          const cacheRestores = [];
          newInterfaceApiSlice.util
            .selectInvalidatedBy(getState(), ["purchaseOrderList"])
            .forEach(({ endpointName, originalArgs }) => {
              if (endpointName !== "getPurchaseOrders") return;
              const previous = new Map();
              dispatch(
                newInterfaceApiSlice.util.updateQueryData(endpointName, originalArgs, draft => {
                  draft.results?.forEach(order => {
                    if (!selected.has(Number(order.id))) return;
                    previous.set(order.id, order.milestone ? current(order.milestone) : null);
                    order.milestone = destinationMilestone;
                  });
                }),
              );
              cacheRestores.push(() => {
                dispatch(
                  newInterfaceApiSlice.util.updateQueryData(endpointName, originalArgs, draft => {
                    draft.results?.forEach(order => {
                      if (previous.has(order.id)) order.milestone = previous.get(order.id);
                    });
                  }),
                );
              });
            });
          return () => {
            cacheRestores.forEach(restoreCache => restoreCache());
            originals.forEach(order => {
              dispatch(patchPurchaseOrderInList(order));
            });
          };
        };

        let restorePurchaseOrders = null;
        if (module === "purchase_order") {
          restorePurchaseOrders = applyPurchaseOrderMilestone();
        }
        try {
          await queryFulfilled;
          if (module === "purchase_order") {
            dispatch(newInterfaceApiSlice.util.invalidateTags(["purchaseOrderList"]));
          } else if (module === "consignment") {
            // Re-apply after save in case a background list GET overwrote the move.
            if (restore) {
              const destination =
                destinationId === "Uncategorized"
                  ? null
                  : (getState().newInterface?.milestoneList || []).find(
                      item => Number(item.id) === Number(destinationId),
                    );
              if (destination !== undefined) {
                applyConsignmentMilestone(destination);
              }
            }
            dispatch(
              newInterfaceApiSlice.util.invalidateTags([
                "consignmentList",
                "filteredConsignmentList",
              ]),
            );
          } else if (module === "transfer_order") {
            dispatch(updateFilterTransferByMilestone(dataFromQuery));
          }
        } catch (error) {
          restore?.();
          restorePurchaseOrders?.();
          dispatch(
            setNotification({
              type: "error",
              message:
                error.error?.data?.detail ||
                "The milestone could not be changed. Please try again.",
            }),
          );
        }
      },
    }),
    getAllFilterCondition: builder.query({
      query: () => "/api_rule/filter_condition/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setAllFilterCondition(result.data)))
          .catch(error => {});
      },
    }),
    getAllFilterField: builder.query({
      query: params => `/api_rule/filter_field/?module=${params}`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setAllFilterField(result.data))).catch(error => {});
      },
    }),
    getAllFilterCriteria: builder.query({
      query: () => "/api_rule/filter_criteria/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setAllFilterField(result.data))).catch(error => {});
      },
    }),
    createFilterCriteria: builder.mutation({
      query: data => ({
        url: `/api_rule/filter_criteria/`,
        method: "POST",
        body: data,
      }),
      onQueryStarted: ({ orderType }, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(res => {
            dispatch(
              setNotification({
                type: "success",
                message: `Filtered ${getNameForViewRule(orderType)} ready—your rules were created successfully!`,
              }),
            );
            dispatch(newInterfaceApiSlice.util.invalidateTags(["viewUpdate"]));
          })
          .catch(error => {
            dispatch(
              setNotification({
                type: "error",
                message:
                  error.error?.data?.detail ||
                  "Could not save view rules. Check the conditions and try again.",
              }),
            );
          });
      },
    }),
    updateFilterCriteria: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api_rule/filter_criteria/${id}/`,
        method: "PUT",
        body: data,
      }),
      onQueryStarted: ({ data: { orderType } }, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(res => {
            dispatch(
              setNotification({
                type: "success",
                message: `Filtered ${getNameForViewRule(orderType)} ready—your rules were updated successfully!`,
              }),
            );
            dispatch(newInterfaceApiSlice.util.invalidateTags(["viewUpdate"]));
          })
          .catch(error => {
            dispatch(
              setNotification({
                type: "error",
                message:
                  error.error?.data?.detail ||
                  "Could not save view rules. Check the conditions and try again.",
              }),
            );
          });
      },
    }),
    deleteFilterCriteria: builder.mutation({
      query: id => ({
        url: `/api_rule/filter_criteria/${id}`,
        method: "DELETE",
        body: {},
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(_ => {
            dispatch(
              setNotification({
                type: "success",
                message: "View reset successfully—back to a fresh start!",
              }),
            );
            dispatch(newInterfaceApiSlice.util.invalidateTags(["viewUpdate"]));
          })
          .catch(err => {
            dispatch(
              setNotification({
                type: "error",
                message: "Error encountered. Please refresh or try again later.",
              }),
            );
          });
      },
    }),
    deleteFilterCriteriaWithoutToast: builder.mutation({
      query: id => ({
        url: `/api_rule/filter_criteria/${id}`,
        method: "DELETE",
        body: {},
      }),
    }),
    getFilterConsignmentsByRule: builder.query({
      query: ({ id, params }) => `/api_shipment/consignment/?view_id=${id}&${params}`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setConsignmentsForRule(result.data)))
          .catch(error => {});
      },
      providesTags: _ => ["filteredConsignmentList"],
    }),
    getCurrentViewDetails: builder.query({
      async queryFn(_arg, { dispatch }) {
        const { authApiSlice } = await import("./authApiSlice");
        const request = dispatch(authApiSlice.endpoints.getDefaultPreference.initiate());
        try {
          const result = await request;
          if (result.error) {
            return { error: result.error };
          }
          dispatch(setViewAndDetailsColumns(result.data));
          return { data: result.data };
        } finally {
          request.unsubscribe();
        }
      },
      providesTags: _ => ["currentViewAndDetails"],
    }),
    updateCurrentViewOrDetails: builder.mutation({
      query: data => ({
        url: `/api_user/miscellaneous/set_default_preference/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(newInterfaceApiSlice.util.invalidateTags(["currentViewAndDetails"]));
      },
    }),
    getStatusForFilterRule: builder.query({
      query: params => params,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setStatusForRule(result.data))).catch(error => {});
      },
    }),
    downloadExportExcelFile: builder.query({
      query: url => ({
        url,
        responseHandler: fileResponseHandler,
      }),
    }),
    updateColumnWidthInList: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api_interface/view/${id}/update_column_width/`,
        method: "PUT",
        body: data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(newInterfaceApiSlice.util.invalidateTags(["viewUpdate"]));
      },
    }),
  }),
});
export const {
  useAddMilestoneMutation,
  useCreateViewMutation,
  useGetMilestonesQuery,
  useUpdateMilestoneDetailsMutation,
  useUpdateNoteMutation,
  useDeleteMilestoneMutation,
  useDeleteNoteMutation,
  useGetViewQuery,
  useGetSingleViewQuery,
  useUpdateViewMutation,
  useDeleteViewMutation,
  useHandleMilestoneViewMutation,
  useGetAllFilterConditionQuery,
  useGetAllFilterFieldQuery,
  useGetAllFilterCriteriaQuery,
  useCreateFilterCriteriaMutation,
  useUpdateFilterCriteriaMutation,
  useDeleteFilterCriteriaMutation,
  useDeleteFilterCriteriaWithoutToastMutation,
  useGetFilterConsignmentsByRuleQuery,
  useGetCurrentViewDetailsQuery,
  useUpdateCurrentViewOrDetailsMutation,
  useGetStatusForFilterRuleQuery,
  useLazyDownloadExportExcelFileQuery,
  useUpdateColumnWidthInListMutation,
} = newInterfaceApiSlice;
